import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ArrowLeft, Wallet, Smartphone, Building2, Clock, ShieldCheck,
  Calendar, AlertTriangle, ArrowRight, CheckCircle2, FileWarning,
} from "lucide-react";
import MarketingLayout from "../components/MarketingLayout";
import { Brand } from "../context/CompanyContext";

/**
 * /pagamentos — public Payments Policy page.
 *
 * Explains accepted payment methods (Bank Transfer + MB WAY), confirmation
 * flow, deadlines, prepayment cases and the cancel/reschedule policy.
 */

const METHODS = [
  {
    key: "bank",
    Icon: Building2,
    title: "Transferência Bancária",
    desc: "Para pagamentos planeados ou de maior valor (subscrições, propriedades premium, faturação para empresas). Os dados bancários (IBAN) são partilhados na fatura ou após confirmação do serviço.",
    perks: ["IBAN partilhado por email", "Comprovativo enviado pelo cliente", "Ideal para faturação a empresas"],
  },
  {
    key: "mbway",
    Icon: Smartphone,
    title: "MB WAY",
    desc: "Pagamento instantâneo, móvel, com confirmação imediata. O método mais usado para serviços avulso e pequenas reservas.",
    perks: ["Confirmação imediata", "Sem dados bancários partilhados", "Para serviços individuais"],
  },
];

export default function PaymentsPolicy() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, []);

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden bg-[#041f1b] py-16 sm:py-24"
               data-testid="payments-page">
        <div aria-hidden
             className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.10),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"
                data-testid="payments-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#a0b2af] hover:text-[#d4af37] transition-colors">
            <ArrowLeft size={14} /> Voltar à Home
          </Link>

          <div className="mt-8">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              <Wallet size={14} /> Política de Pagamentos
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight text-[#fdfbf7]">
              Pagamentos transparentes &amp; <span className="italic text-[#d4af37]">simples</span>
            </h1>
            <p className="mt-5 text-base text-[#a0b2af] leading-relaxed max-w-2xl">
              Acreditamos que pagar deve ser tão tranquilo como o serviço em si. Em baixo
              encontra os métodos aceites, prazos e a nossa política de cancelamento.
            </p>
          </div>

          {/* Methods */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5"
               data-testid="payments-methods">
            {METHODS.map(m => {
              const Icon = m.Icon;
              return (
                <article key={m.key}
                         data-testid={`payments-method-${m.key}`}
                         className="rounded-2xl border border-[#1a4640] bg-[#062c27] p-6 hover:border-[#d4af37]/60 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#d4af37]/10 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center w-12 h-12 rounded-full bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30">
                      <Icon size={22} />
                    </span>
                    <h2 className="font-display text-xl text-[#fdfbf7]">{m.title}</h2>
                  </div>
                  <p className="mt-4 text-sm text-[#a0b2af] leading-relaxed">{m.desc}</p>
                  <ul className="mt-4 space-y-2">
                    {m.perks.map(p => (
                      <li key={p} className="flex items-start gap-2 text-sm text-[#fdfbf7]/90">
                        <CheckCircle2 size={14} className="text-[#d4af37] shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          {/* Flow */}
          <div className="mt-14">
            <h2 className="font-display text-2xl text-[#fdfbf7]">Como funciona o pagamento</h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3"
                 data-testid="payments-flow">
              {[
                { step: "1", title: "Pedido", desc: "Reserva ou pedido de serviço submetido." },
                { step: "2", title: "Confirmação", desc: "Confirmamos disponibilidade e enviamos os dados." },
                { step: "3", title: "Serviço", desc: "Limpeza realizada com supervisão e relatório." },
                { step: "4", title: "Pagamento", desc: "Após o serviço — ou antecipado para subscrições." },
              ].map(s => (
                <div key={s.step}
                     className="relative rounded-xl border border-[#1a4640] bg-[#062c27] p-4">
                  <span className="font-display text-3xl text-[#d4af37]/30 absolute right-3 top-2">{s.step}</span>
                  <h3 className="font-display text-base text-[#fdfbf7]">{s.title}</h3>
                  <p className="mt-1.5 text-xs text-[#a0b2af] leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Deadlines + advance */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Block icon={<Clock size={18} />} title="Prazos de pagamento">
              <ul className="space-y-2.5 text-sm text-[#fdfbf7]/90">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#d4af37] shrink-0 mt-0.5" />Serviços avulso: pagamento até <strong>5 dias úteis</strong> após o serviço.</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#d4af37] shrink-0 mt-0.5" />Subscrições mensais: pagamento <strong>antecipado</strong> nos primeiros 5 dias do mês.</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#d4af37] shrink-0 mt-0.5" />Propriedades premium / villas: pode ser solicitado <strong>sinal de 30%</strong> antes da reserva.</li>
              </ul>
            </Block>
            <Block icon={<ShieldCheck size={18} />} title="Confirmação manual">
              <p className="text-sm text-[#a0b2af] leading-relaxed">
                Todos os pagamentos por transferência bancária são <strong className="text-[#fdfbf7]/90">confirmados manualmente</strong> pela nossa equipa após o envio do comprovativo (carregado na sua área de cliente ou por email). Pagamentos MB WAY são confirmados automaticamente em segundos.
              </p>
              <p className="mt-3 text-sm text-[#a0b2af]">
                Receberá sempre uma confirmação por email assim que o pagamento for validado.
              </p>
            </Block>
          </div>

          {/* Cancellation */}
          <div className="mt-12">
            <h2 className="font-display text-2xl text-[#fdfbf7] flex items-center gap-2">
              <Calendar size={20} className="text-[#d4af37]" />
              Cancelamentos e reagendamentos
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3"
                 data-testid="payments-cancellation">
              <PolicyCard tone="green" title="Mais de 48h antes" desc="Cancelamento ou reagendamento gratuito, sem custos." />
              <PolicyCard tone="amber" title="Entre 24h e 48h" desc="Cobrança parcial de 50% para cobrir logística e equipa." />
              <PolicyCard tone="red"   title="Menos de 24h" desc="Cobrança integral do serviço (no-show / last-minute)." />
            </div>
          </div>

          {/* Clients without service contract */}
          <div className="mt-12" data-testid="payments-no-contract">
            <h2 className="font-display text-2xl text-[#fdfbf7] flex items-center gap-2">
              <FileWarning size={20} className="text-[#d4af37]" />
              Clientes sem contrato de prestação de serviços
            </h2>
            <p className="mt-3 text-sm text-[#a0b2af] leading-relaxed max-w-3xl">
              Política aplicável aos clientes que utilizam os serviços da <Brand />
              sem um contrato de prestação de serviços em vigor.
            </p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <article className="rounded-2xl border-2 border-[#d4af37]/40 bg-[#d4af37]/5 p-5">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-[#d4af37]" />
                  <h3 className="font-display text-base text-[#fdfbf7]">Prazo de regularização</h3>
                </div>
                <p className="mt-3 text-sm text-[#fdfbf7]/90 leading-relaxed">
                  Os pagamentos deverão estar <strong className="text-[#d4af37]">integralmente regularizados até 48 horas antes</strong> da data do próximo serviço agendado.
                </p>
              </article>

              <article className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-amber-400" />
                  <h3 className="font-display text-base text-[#fdfbf7]">Valores em atraso</h3>
                </div>
                <p className="mt-3 text-sm text-amber-100/90 leading-relaxed">
                  A existência de qualquer valor em atraso poderá <strong>comprometer a reserva da equipa</strong> e a realização do serviço seguinte. A <Brand /> não fica obrigada a garantir a manutenção do agendamento enquanto subsistirem pagamentos pendentes.
                </p>
              </article>

              <article className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/5 p-5">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-300" />
                  <h3 className="font-display text-base text-[#fdfbf7]">Confirmação do serviço</h3>
                </div>
                <p className="mt-3 text-sm text-emerald-100/90 leading-relaxed">
                  A confirmação do serviço apenas será efetuada após a <strong>regularização dos valores em dívida</strong> e, quando aplicável, mediante o envio do <strong>respetivo comprovativo de pagamento</strong>.
                </p>
              </article>

              <article className="rounded-2xl border border-[#1a4640] bg-[#062c27] p-5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#d4af37]" />
                  <h3 className="font-display text-base text-[#fdfbf7]">Objetivo desta política</h3>
                </div>
                <p className="mt-3 text-sm text-[#a0b2af] leading-relaxed">
                  Assegurar uma <strong className="text-[#fdfbf7]/90">gestão operacional eficiente</strong>, garantir a disponibilidade da equipa e manter a <strong className="text-[#fdfbf7]/90">qualidade do serviço</strong> prestado a todos os clientes.
                </p>
              </article>
            </div>

            <div className="mt-5 rounded-2xl border-2 border-red-500/40 bg-red-500/5 p-5 flex items-start gap-3"
                 data-testid="payments-no-contract-suspension">
              <AlertTriangle size={20} className="text-red-300 shrink-0 mt-0.5" />
              <p className="text-sm text-red-100/95 leading-relaxed">
                <strong className="text-red-200">Suspensão de serviços futuros · </strong>
                Caso o pagamento não seja regularizado até <strong>48 horas antes</strong> do próximo
                serviço agendado, a <Brand /> reserva-se o direito de{" "}
                <strong>cancelar ou de não garantir a realização de qualquer serviço ou agendamento futuro</strong>{" "}
                associado à propriedade, até que todos os valores em dívida se encontrem{" "}
                <strong>integralmente regularizados</strong>.
              </p>
            </div>

            <p className="mt-5 text-xs text-[#a0b2af] italic">
              Para evitar constrangimentos, recomendamos a formalização de um contrato de prestação de serviços —
              <Link to="/contacto" className="text-[#d4af37] hover:underline ml-1">fale connosco</Link> para condições personalizadas.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 flex items-start gap-3"
               data-testid="payments-disclaimer">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-100/90 leading-relaxed">
              Em caso de divergência sobre um pagamento ou serviço, contacte-nos primeiro por
              email ou WhatsApp — resolveremos a maioria das situações em 24h. Esta política
              não substitui qualquer direito reconhecido por lei aos consumidores.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-14 rounded-2xl border border-[#1a4640] bg-[#062c27] p-8 text-center"
               data-testid="payments-cta">
            <h3 className="font-display text-2xl text-[#fdfbf7]">Dúvidas sobre pagamentos?</h3>
            <p className="mt-3 text-sm text-[#a0b2af] max-w-xl mx-auto">
              Fale connosco — respondemos em 24h e ajudamos a encontrar a melhor solução para si.
            </p>
            <Link to="/contacto"
                  data-testid="payments-cta-contact"
                  className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold uppercase tracking-widest text-xs bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all">
              Contacte-nos <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Block({ title, icon, children }) {
  return (
    <article className="rounded-2xl border border-[#1a4640] bg-[#062c27] p-6">
      <h2 className="font-display text-lg text-[#fdfbf7] flex items-center gap-2">
        <span className="text-[#d4af37]">{icon}</span> {title}
      </h2>
      <div className="mt-4">{children}</div>
    </article>
  );
}

function PolicyCard({ tone, title, desc }) {
  const palettes = {
    green: "border-emerald-500/40 bg-emerald-500/5 text-emerald-200",
    amber: "border-amber-500/40 bg-amber-500/5 text-amber-200",
    red:   "border-red-500/40 bg-red-500/5 text-red-200",
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${palettes[tone]}`}>
      <h3 className="font-display text-base text-[#fdfbf7]">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
